import {
  ArrowRight,
  CheckCircle2,
  CircleDollarSign,
  FileText,
  History,
  Layers3,
  Plus,
  RefreshCw,
  Send,
  Sparkles,
  Target,
  UserRound,
  Workflow,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createAdminProposal, getAdminLeads, getAdminProposals, requestJson } from "../../lib/api";
import type {
  AdminAgreementRecord,
  AdminLeadRecord,
  AdminPaymentRecord,
  AdminProposalRecord,
  ClientRecord,
  JourneyProposal,
  JourneyProposalItem,
  JourneySummary,
} from "../../lib/types";
import { TRUEVANCE_BRAND_NAME, TruevanceBrandHeader, useTruevanceBranding } from "./TruevanceBranding";
import { formatCurrency, formatDisplayDate, formatPercent } from "./TruevanceConnectData";

type ProposalLineForm = {
  id: string;
  name: string;
  description: string;
  quantity: string;
  unitCents: string;
  lineType: string;
};

type ProposalForm = {
  leadId: string;
  clientId: string;
  packageName: string;
  recommendedServices: string;
  selectedServices: string;
  recommendedAddons: string;
  notes: string;
  expirationDate: string;
  discountDollars: string;
  depositDollars: string;
  statusMode: "draft" | "ready";
};

type ProposalCenterDetail = {
  proposal: JourneyProposal;
  lead: AdminLeadRecord | null;
  client: ClientRecord | null;
  agreement: AdminAgreementRecord | null;
  payment: AdminPaymentRecord | null;
  journey: JourneySummary | null;
  timeline: Array<{
    id: string;
    leadId: string;
    proposalId: string | null;
    agreementId: string | null;
    paymentId: string | null;
    activityType: string;
    notes: string;
    relatedLink: string;
    createdAt: string;
  }>;
  versionHistory: Array<{
    label: string;
    status: string;
    detail: string;
    createdAt: string | null;
  }>;
};

const proposalStatusStages = [
  "Draft",
  "Proposal Approved to Send",
  "Proposal Sent",
  "Proposal Viewed",
  "Proposal Revision Requested",
  "Proposal Accepted",
  "Proposal Declined",
] as const;

const workflowStages = [
  "Proposal Created",
  "Proposal Approved to Send",
  "Proposal Sent",
  "Proposal Viewed",
  "Proposal Revision Requested",
  "Proposal Accepted",
  "Client Created",
  "Agreement Sent",
  "Project Handoff Ready",
] as const;

function blankLine(): ProposalLineForm {
  return {
    id: crypto.randomUUID(),
    name: "Truevance Consulting Package",
    description: "Primary consulting service package.",
    quantity: "1",
    unitCents: "0",
    lineType: "service",
  };
}

function formatMoney(cents: number) {
  return formatCurrency(Math.max(0, Number(cents) || 0));
}

function proposalStatusTone(status: string) {
  if (status === "Proposal Accepted") return "border-[#22E0D0]/35 bg-[#22E0D0]/12 text-[#F5F7FA]";
  if (status === "Proposal Sent" || status === "Proposal Approved to Send") return "border-[#FF6A2B]/35 bg-[#FF6A2B]/12 text-[#F5F7FA]";
  if (status === "Proposal Revision Requested" || status === "Proposal Declined") return "border-[rgba(214,178,74,.18)] bg-[rgba(255,255,255,.06)] text-[#F5F7FA]";
  return "border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.05)] text-[#F5F7FA]";
}

export function TruevanceProposalCenterPage() {
  const navigate = useNavigate();
  const { proposalId: proposalIdParam } = useParams<{ proposalId?: string }>();
  const [leads, setLeads] = useState<AdminLeadRecord[]>([]);
  const [proposals, setProposals] = useState<AdminProposalRecord[]>([]);
  const [detail, setDetail] = useState<ProposalCenterDetail | null>(null);
  const [selectedProposalId, setSelectedProposalId] = useState(proposalIdParam || "");
  const [editingProposalId, setEditingProposalId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  const [form, setForm] = useState<ProposalForm>({
    leadId: "",
    clientId: "",
    packageName: "Truevance Consulting Proposal",
    recommendedServices: "",
    selectedServices: "",
    recommendedAddons: "",
    notes: "",
    expirationDate: "",
    discountDollars: "0",
    depositDollars: "",
    statusMode: "draft",
  });
  const [lines, setLines] = useState<ProposalLineForm[]>([blankLine()]);

  useTruevanceBranding(
    "Proposal Center",
    "Proposal creation, version history, approvals, acceptance workflow, and client conversion for Truevance Connect Business Center.",
  );

  useEffect(() => {
    setSelectedProposalId(proposalIdParam || "");
  }, [proposalIdParam]);

  useEffect(() => {
    void loadWorkspace(proposalIdParam || selectedProposalId || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedProposalId) {
      setDetail(null);
      setEditingProposalId(null);
      return;
    }
    void loadDetail(selectedProposalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProposalId]);

  const proposalMetrics = useMemo(() => {
    const total = proposals.length;
    const readyToSend = proposals.filter((proposal) => proposal.status === "Proposal Approved to Send").length;
    const accepted = proposals.filter((proposal) => proposal.status === "Proposal Accepted").length;
    const revisions = proposals.filter((proposal) => proposal.status === "Proposal Revision Requested").length;
    const sent = proposals.filter((proposal) => proposal.status === "Proposal Sent").length;
    const acceptedRate = total ? (accepted / total) * 100 : 0;
    return { total, readyToSend, accepted, revisions, sent, acceptedRate };
  }, [proposals]);

  const totals = useMemo(() => {
    const subtotal = lines.reduce((sum, line) => {
      const quantity = Math.max(1, Number(line.quantity) || 1);
      const unitCents = Math.max(0, Math.round((Number(line.unitCents) || 0) * 100));
      return sum + quantity * unitCents;
    }, 0);
    const discountCents = Math.max(0, Math.round((Number(form.discountDollars) || 0) * 100));
    const depositCents = Math.max(0, Math.round((Number(form.depositDollars) || 0) * 100));
    const totalCents = Math.max(0, subtotal - discountCents);
    const balanceCents = Math.max(0, totalCents - depositCents);
    return { subtotal, discountCents, depositCents, totalCents, balanceCents };
  }, [form.discountDollars, form.depositDollars, lines]);

  async function loadWorkspace(preferredProposalId = "") {
    setLoading(true);
    setError("");
    try {
      const [leadData, proposalData] = await Promise.all([getAdminLeads(), getAdminProposals()]);
      setLeads(leadData.leads || []);
      setProposals(proposalData.proposals || []);

      const nextSelectedId = preferredProposalId || proposalIdParam || proposalData.proposals?.[0]?.id || "";
      if (nextSelectedId) {
        setSelectedProposalId(nextSelectedId);
        await loadDetail(nextSelectedId);
      } else {
        setDetail(null);
        setEditingProposalId(null);
      }

      if (!form.leadId && leadData.leads?.length) {
        setForm((current) => ({ ...current, leadId: leadData.leads[0].id }));
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load proposal workspace.");
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(proposalId: string) {
    setDetailLoading(true);
    try {
      const response = await requestJson<ProposalCenterDetail>(`/api/admin/proposals/${proposalId}`);
      setDetail(response);
      hydrateEditorFromDetail(response);
      setActionNotes("");
    } catch (loadError) {
      setDetail(null);
      setError(loadError instanceof Error ? loadError.message : "Unable to load proposal detail.");
    } finally {
      setDetailLoading(false);
    }
  }

  async function submitProposal(statusMode: "draft" | "ready") {
    if (!form.leadId) {
      setError("Please choose an existing lead before creating the proposal.");
      return;
    }
    if (!form.packageName.trim()) {
      setError("Please enter a proposal package name.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");
    try {
      if (editingProposalId) {
        await requestJson<ProposalCenterDetail>(`/api/admin/proposals/${editingProposalId}`, {
          method: "POST",
          body: JSON.stringify({
            action: "update",
            leadId: form.leadId,
            clientId: form.clientId || undefined,
            packageName: form.packageName,
            recommendedServices: form.recommendedServices,
            selectedServices: form.selectedServices,
            recommendedAddons: form.recommendedAddons,
            notes: form.notes,
            expirationDate: form.expirationDate || null,
            subtotalCents: totals.subtotal,
            discountCents: totals.discountCents,
            depositCents: totals.depositCents,
            totalCents: totals.totalCents,
            lineItems: lines.map((line) => ({
              name: line.name,
              description: line.description,
              quantity: Number(line.quantity) || 1,
              unitCents: Math.round((Number(line.unitCents) || 0) * 100),
              lineType: line.lineType,
            })),
          }),
        });
        setMessage("Proposal changes saved.");
        await loadWorkspace(editingProposalId);
      } else {
        const created = await createAdminProposal({
          action: statusMode === "ready" ? "ready_to_send" : "save_draft",
          leadId: form.leadId,
          clientId: form.clientId || undefined,
          packageName: form.packageName,
          recommendedServices: form.recommendedServices,
          selectedServices: form.selectedServices,
          recommendedAddons: form.recommendedAddons,
          notes: form.notes,
          expirationDate: form.expirationDate || undefined,
          subtotalCents: totals.subtotal,
          discountCents: totals.discountCents,
          depositCents: totals.depositCents,
          totalCents: totals.totalCents,
          lineItems: lines.map((line) => ({
            name: line.name,
            description: line.description,
            quantity: Number(line.quantity) || 1,
            unitCents: Math.round((Number(line.unitCents) || 0) * 100),
            lineType: line.lineType,
          })),
        });

        setMessage(statusMode === "ready" ? "Proposal saved and marked ready to send." : "Proposal draft saved.");
        setForm((current) => ({
          ...current,
          packageName: "Truevance Consulting Proposal",
          recommendedServices: "",
          selectedServices: "",
          recommendedAddons: "",
          notes: "",
          expirationDate: "",
          discountDollars: "0",
          depositDollars: "",
          statusMode,
        }));
        setLines([blankLine()]);
        navigate(`/admin/truevance-proposal-center/${created.proposal.id}`);
        await loadWorkspace(created.proposal.id);
      }
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to save proposal.");
    } finally {
      setSaving(false);
    }
  }

  function hydrateEditorFromDetail(record: ProposalCenterDetail) {
    setEditingProposalId(record.proposal.id);
    setForm({
      leadId: record.proposal.leadId,
      clientId: record.proposal.clientId || "",
      packageName: record.proposal.packageName || "Truevance Consulting Proposal",
      recommendedServices: record.proposal.recommendedServices || "",
      selectedServices: record.proposal.selectedServices || "",
      recommendedAddons: record.proposal.recommendedAddons || "",
      notes: record.proposal.notes || "",
      expirationDate: record.proposal.expirationDate || "",
      discountDollars: String((record.proposal.discountCents || 0) / 100),
      depositDollars: String((record.proposal.depositCents || 0) / 100),
      statusMode: "draft",
    });
    setLines(
      record.proposal.lineItems.length
        ? record.proposal.lineItems.map((item) => ({
            id: item.id,
            name: item.name,
            description: item.description,
            quantity: String(item.quantity),
            unitCents: String((item.unitCents || 0) / 100),
            lineType: item.lineType,
          }))
        : [blankLine()],
    );
  }

  function startNewProposal() {
    setSelectedProposalId("");
    setEditingProposalId(null);
    setDetail(null);
    setActionNotes("");
    setMessage("");
    setError("");
    setForm({
      leadId: leads[0]?.id || "",
      clientId: "",
      packageName: "Truevance Consulting Proposal",
      recommendedServices: "",
      selectedServices: "",
      recommendedAddons: "",
      notes: "",
      expirationDate: "",
      discountDollars: "0",
      depositDollars: "",
      statusMode: "draft",
    });
    setLines([blankLine()]);
    navigate("/admin/truevance-proposal-center");
  }

  async function updateProposal(action: "sent" | "revision_requested" | "accepted" | "declined") {
    if (!detail?.proposal.id) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await requestJson<ProposalCenterDetail>(`/api/admin/proposals/${detail.proposal.id}`, {
        method: "POST",
        body: JSON.stringify({
          action,
          notes: actionNotes || undefined,
        }),
      });
      const labels = {
        sent: "Proposal marked as sent.",
        revision_requested: "Revision request recorded.",
        accepted: "Proposal accepted and client conversion completed.",
        declined: "Proposal marked as declined.",
      } as const;
      setMessage(labels[action]);
      await loadWorkspace(detail.proposal.id);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Unable to update proposal status.");
    } finally {
      setSaving(false);
    }
  }

  const selectedLead = leads.find((lead) => lead.id === form.leadId);

  return (
    <main className="truevance-shell truevance-business-shell min-h-screen text-[#F5F7FA]">
      <TruevanceBrandHeader />

      <section className="truevance-business-hero border-b border-[rgba(34,224,208,.16)] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
          <div className="grid gap-4">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[rgba(34,224,208,.32)] bg-[rgba(7,26,47,.55)] px-3 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#22E0D0]">
              <Sparkles className="size-4" aria-hidden="true" />
              {TRUEVANCE_BRAND_NAME}
            </div>
            <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">Proposal Center</h1>
            <p className="max-w-3xl text-base font-semibold leading-8 text-[#E8EEF4]">
              Build proposals, track versions, manage approvals, request revisions, and convert accepted work into client records
              for the next Project Center handoff.
            </p>
          </div>

          <article className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#22E0D0]">Operational focus</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-[#F5F7FA]">Proposal lifecycle and conversion</h2>
            <p className="mt-3 text-sm font-semibold leading-7 text-[#E8EEF4]">
              The public site attracts clients. This center manages the consulting proposal workflow, client conversion, and the
              record handoff into the Business Center.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link to="/admin/truevance-business-center" className="admin-primary-button focus-ring">
                Business Center Home
              </Link>
              <Link to="/admin/truevance-project-center" className="admin-secondary-button focus-ring">
                Project Center
              </Link>
            </div>
          </article>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          {error ? (
            <section className="truevance-business-card truevance-business-card--light rounded-2xl border border-[rgba(214,178,74,.2)] p-4 text-[#071A2F]">
              <p className="text-sm font-bold">Proposal Center notice</p>
              <p className="mt-2 text-sm font-semibold text-[#334155]">{error}</p>
              <button type="button" className="admin-secondary-button focus-ring mt-4" onClick={() => setError("")}>
                Dismiss
              </button>
            </section>
          ) : null}

          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <MetricCard label="Proposal Records" value={proposalMetrics.total} note="Persistent consulting proposal records." tone="dark" />
            <MetricCard label="Ready to Send" value={proposalMetrics.readyToSend} note="Approved for publishing." tone="light" />
            <MetricCard label="Sent Proposals" value={proposalMetrics.sent} note="With a live review link." tone="light" />
            <MetricCard label="Accepted Proposals" value={proposalMetrics.accepted} note="Converted into client records." tone="dark" />
            <MetricCard label="Acceptance Rate" value={formatPercent(proposalMetrics.acceptedRate)} note="Accepted over total proposals." tone="light" />
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
            <article className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
              <SectionHeading
                icon={FileText}
                title={editingProposalId ? "Edit Proposal" : "Create Proposal"}
                subtitle={
                  editingProposalId
                    ? "Update the selected proposal, keep historical pricing intact for accepted records, and preserve the workflow trail."
                    : "Choose an existing lead, build the scope, calculate pricing, and save the proposal as a draft or ready-to-send record."
                }
              />
              {editingProposalId ? (
                <div className="mt-5 rounded-2xl border border-[rgba(34,224,208,.18)] bg-[rgba(7,26,47,.05)] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#D94A16]">Editing existing proposal</p>
                  <p className="mt-2 text-sm font-semibold text-[#5c5246]">
                    Changes save to the selected proposal record. Accepted proposals are locked to preserve historical pricing.
                  </p>
                  <button type="button" onClick={startNewProposal} className="admin-secondary-button focus-ring mt-4">
                    Create New Proposal
                  </button>
                </div>
              ) : null}
              <form className="mt-6 grid gap-4" onSubmit={(event) => event.preventDefault()}>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Existing Lead">
                    <select
                      value={form.leadId}
                      onChange={(event) => setForm((state) => ({ ...state, leadId: event.target.value }))}
                      className="admin-input"
                    >
                      <option value="">Select a lead</option>
                      {leads.map((lead) => (
                        <option key={lead.id} value={lead.id}>
                          {lead.fullName} · {lead.email}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Client ID / Record Link">
                    <input
                      value={form.clientId}
                      onChange={(event) => setForm((state) => ({ ...state, clientId: event.target.value }))}
                      className="admin-input"
                      placeholder="Optional client record ID"
                    />
                  </Field>
                  <Field label="Proposal Package Name">
                    <input
                      value={form.packageName}
                      onChange={(event) => setForm((state) => ({ ...state, packageName: event.target.value }))}
                      className="admin-input"
                      placeholder="Truevance Consulting Proposal"
                    />
                  </Field>
                  <Field label="Expiration Date">
                    <input
                      type="date"
                      value={form.expirationDate}
                      onChange={(event) => setForm((state) => ({ ...state, expirationDate: event.target.value }))}
                      className="admin-input"
                    />
                  </Field>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <Field label="Discount">
                    <NumberInput
                      value={form.discountDollars}
                      onChange={(value) => setForm((state) => ({ ...state, discountDollars: value }))}
                      prefix="$"
                    />
                  </Field>
                  <Field label="Deposit">
                    <NumberInput
                      value={form.depositDollars}
                      onChange={(value) => setForm((state) => ({ ...state, depositDollars: value }))}
                      prefix="$"
                    />
                  </Field>
                  <Field label="Selected Services">
                    <textarea
                      rows={4}
                      value={form.selectedServices}
                      onChange={(event) => setForm((state) => ({ ...state, selectedServices: event.target.value }))}
                      className="admin-input"
                      placeholder="Website redesign, portal build, automation support..."
                    />
                  </Field>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Recommended Services">
                    <textarea
                      rows={4}
                      value={form.recommendedServices}
                      onChange={(event) => setForm((state) => ({ ...state, recommendedServices: event.target.value }))}
                      className="admin-input"
                      placeholder="Services recommended after assessment"
                    />
                  </Field>
                  <Field label="Recommended Add-ons">
                    <textarea
                      rows={4}
                      value={form.recommendedAddons}
                      onChange={(event) => setForm((state) => ({ ...state, recommendedAddons: event.target.value }))}
                      className="admin-input"
                      placeholder="Extra coverage, training, maintenance, strategy sessions..."
                    />
                  </Field>
                </div>

                <Field label="Proposal Notes">
                  <textarea
                    rows={4}
                    value={form.notes}
                    onChange={(event) => setForm((state) => ({ ...state, notes: event.target.value }))}
                    className="admin-input"
                    placeholder="Assumptions, approvals, and internal context..."
                  />
                </Field>

                <div className="border-t border-[rgba(214,178,74,.16)] pt-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#D94A16]">Line Items</p>
                      <p className="mt-1 text-sm font-semibold text-[#5c5246]">
                        Add the services and deliverables that make up the proposal total.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLines((current) => [...current, blankLine()])}
                      className="admin-secondary-button focus-ring"
                    >
                      <Plus className="size-4" aria-hidden="true" />
                      Add Line Item
                    </button>
                  </div>

                  <div className="mt-4 grid gap-3">
                    {lines.map((line, index) => (
                      <div key={line.id} className="rounded-2xl border border-[rgba(217,74,22,.16)] bg-white p-4 shadow-[0_10px_28px_rgba(7,26,47,.05)]">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <p className="text-sm font-black uppercase tracking-[0.12em] text-[#D94A16]">Line Item {index + 1}</p>
                          <button
                            type="button"
                            onClick={() =>
                              setLines((current) => {
                                const next = current.filter((entry) => entry.id !== line.id);
                                return next.length ? next : [blankLine()];
                              })
                            }
                            className="inline-flex items-center gap-2 rounded-full border border-red-300 px-3 py-2 text-xs font-black uppercase tracking-[0.12em] text-red-700 transition hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                          <Field label="Item Name">
                            <input
                              value={line.name}
                              onChange={(event) =>
                                setLines((current) => current.map((entry) => (entry.id === line.id ? { ...entry, name: event.target.value } : entry)))
                              }
                              className="admin-input"
                              placeholder="Website strategy"
                            />
                          </Field>
                          <Field label="Line Type">
                            <input
                              value={line.lineType}
                              onChange={(event) =>
                                setLines((current) => current.map((entry) => (entry.id === line.id ? { ...entry, lineType: event.target.value } : entry)))
                              }
                              className="admin-input"
                              placeholder="service"
                            />
                          </Field>
                          <Field label="Quantity">
                            <NumberInput
                              value={line.quantity}
                              onChange={(value) =>
                                setLines((current) => current.map((entry) => (entry.id === line.id ? { ...entry, quantity: value } : entry)))
                              }
                            />
                          </Field>
                          <Field label="Unit Price">
                            <NumberInput
                              value={line.unitCents}
                              onChange={(value) =>
                                setLines((current) => current.map((entry) => (entry.id === line.id ? { ...entry, unitCents: value } : entry)))
                              }
                              prefix="$"
                            />
                          </Field>
                        </div>
                        <div className="mt-4">
                          <Field label="Description">
                            <textarea
                              rows={3}
                              value={line.description}
                              onChange={(event) =>
                                setLines((current) => current.map((entry) => (entry.id === line.id ? { ...entry, description: event.target.value } : entry)))
                              }
                              className="admin-input"
                              placeholder="Scope, deliverables, and notes..."
                            />
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-4">
                  <MetricCard label="Subtotal" value={formatMoney(totals.subtotal)} note="Calculated from line items." tone="dark" />
                  <MetricCard label="Discount" value={formatMoney(totals.discountCents)} note="Manual discount adjustment." tone="light" />
                  <MetricCard label="Total" value={formatMoney(totals.totalCents)} note="Subtotal minus discount." tone="light" />
                  <MetricCard label="Balance Due" value={formatMoney(totals.balanceCents)} note="Remaining balance after deposit." tone="dark" />
                </div>

                {selectedLead ? (
                  <div className="rounded-2xl border border-[rgba(34,224,208,.18)] bg-[rgba(7,26,47,.05)] p-4">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#D94A16]">Selected Lead</p>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-bold text-[#071A2F]">{selectedLead.fullName}</p>
                        <p className="text-sm text-[#5c5246]">
                          {selectedLead.email} · {selectedLead.source}
                        </p>
                      </div>
                      <Link to={`/admin/leads/${selectedLead.id}`} className="text-sm font-bold text-[#D94A16] hover:underline">
                        Open Lead Record
                      </Link>
                    </div>
                  </div>
                ) : null}

                {message ? <p className="rounded-2xl border border-[rgba(34,224,208,.18)] bg-[rgba(34,224,208,.08)] px-4 py-3 text-sm font-bold text-[#071A2F]">{message}</p> : null}

                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={() => void submitProposal("draft")} disabled={saving} className="admin-primary-button focus-ring">
                    {saving ? "Saving..." : editingProposalId ? "Save Changes" : "Save Draft"}
                  </button>
                  {!editingProposalId ? (
                    <button type="button" onClick={() => void submitProposal("ready")} disabled={saving} className="admin-secondary-button focus-ring">
                      <Send className="size-4" aria-hidden="true" />
                      {saving ? "Saving..." : "Mark Ready to Send"}
                    </button>
                  ) : null}
                </div>
              </form>
            </article>

            <aside className="grid gap-6">
              <article className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
                <SectionHeading
                  icon={Layers3}
                  title="Proposal Status Workflow"
                  subtitle="Track the path from draft through acceptance and client conversion."
                  dark
                />
                <div className="mt-5 grid gap-3">
                  {proposalStatusStages.map((status) => {
                    const count = proposals.filter((proposal) => proposal.status === status).length;
                    return (
                      <div
                        key={status}
                        className="flex items-center justify-between gap-3 rounded-2xl border border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.05)] px-4 py-3"
                      >
                        <span className="text-sm font-bold text-[#F5F7FA]">{status}</span>
                        <span className="rounded-full border border-[rgba(214,178,74,.24)] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#E0C96A]">
                          {count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </article>

              <article className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
                <SectionHeading
                  icon={FileText}
                  title="Proposal Records"
                  subtitle="Review live proposal records, status, totals, and direct links to the client-facing review pages."
                />
                <div className="mt-5 grid gap-3">
                  {proposals.length ? (
                    proposals.slice(0, 12).map((proposal) => {
                      const selected = proposal.id === selectedProposalId;
                      return (
                        <button
                          key={proposal.id}
                          type="button"
                          onClick={() => {
                            setSelectedProposalId(proposal.id);
                            navigate(`/admin/truevance-proposal-center/${proposal.id}`);
                          }}
                          className={`grid gap-3 rounded-2xl border p-4 text-left transition hover:-translate-y-0.5 ${
                            selected
                              ? "border-[rgba(34,224,208,.48)] bg-[rgba(34,224,208,.08)] shadow-[0_20px_45px_rgba(7,26,47,.08)]"
                              : "border-[rgba(217,74,22,.18)] bg-white hover:border-[rgba(214,178,74,.45)] hover:shadow-[0_20px_45px_rgba(7,26,47,.08)]"
                          }`}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{proposal.proposalNumber}</p>
                              <p className="mt-1 font-display text-2xl font-bold text-[#071A2F]">{proposal.leadName}</p>
                              <p className="text-sm font-semibold text-[#5c5246]">
                                {proposal.leadEmail} · {proposal.leadSource}
                              </p>
                            </div>
                            <span className={`rounded-full border px-3 py-1 text-xs font-black uppercase tracking-[0.12em] ${proposalStatusTone(proposal.status)}`}>
                              {proposal.status}
                            </span>
                          </div>
                          <div className="grid gap-1 text-sm font-semibold text-[#5c5246]">
                            <p>{proposal.packageName || "Custom proposal package"}</p>
                            <p>
                              {formatMoney(proposal.totalCents)} total · {proposal.lineItemCount} line item
                              {proposal.lineItemCount === 1 ? "" : "s"}
                            </p>
                            <p>Created {formatDisplayDate(proposal.createdAt)}</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <a
                              href={proposal.reviewUrl || `/proposal/${proposal.token}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-[rgba(217,74,22,.18)] bg-white px-4 text-sm font-extrabold text-[#071A2F] transition hover:border-[#FF6A2B] hover:bg-[#fffdf8]"
                              title="Open the proposal review URL"
                            >
                              Open Review Link
                            </a>
                            <Link
                              to={`/admin/leads/${proposal.leadId}`}
                              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-[rgba(217,74,22,.18)] bg-white px-4 text-sm font-extrabold text-[#071A2F] transition hover:border-[#FF6A2B] hover:bg-[#fffdf8]"
                              title="Open the linked lead record"
                            >
                              Open Lead
                            </Link>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <EmptyState title="No proposal records yet" description="Create the first proposal draft and it will appear here for workflow tracking." />
                  )}
                </div>
              </article>
            </aside>
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.02fr_.98fr]">
            <article className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
              <SectionHeading
                icon={History}
                title="Proposal Version History"
                subtitle="Each proposal change is tracked through workflow milestones so the record can be reviewed before acceptance."
                dark
              />
              <div className="mt-5 grid gap-3">
                {detail?.versionHistory?.length ? (
                  detail.versionHistory.map((version) => (
                    <div key={`${version.label}-${version.status}`} className="rounded-2xl border border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.05)] p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.14em] text-[#22E0D0]">{version.label}</p>
                          <h3 className="mt-1 font-display text-2xl font-bold text-[#F5F7FA]">{version.status}</h3>
                        </div>
                        <span className="rounded-full border border-[rgba(214,178,74,.24)] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#E0C96A]">
                          {version.createdAt ? formatDisplayDate(version.createdAt) : "Pending"}
                        </span>
                      </div>
                      <p className="mt-3 text-sm font-semibold leading-7 text-[#E8EEF4]">{version.detail}</p>
                    </div>
                  ))
                ) : (
                  <EmptyState
                    title="Version history appears after a proposal is loaded"
                    description="Select a proposal from the records list to review its lifecycle milestones and acceptance history."
                    dark
                  />
                )}
              </div>
            </article>

            <article className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
              <SectionHeading
                icon={Workflow}
                title="Selected Proposal Detail"
                subtitle="Review line items, record links, client conversion, and the operational handoff to Project Center."
              />

              {detail ? (
                <div className="mt-6 grid gap-6">
                  <div className="grid gap-4 md:grid-cols-2">
                    <DetailCard label="Proposal" value={detail.proposal.proposalNumber} note={detail.proposal.packageName} />
                    <DetailCard label="Status" value={detail.proposal.status} note={detail.proposal.reviewUrl ? "Client review link available." : "Review link will be created automatically."} />
                    <DetailCard label="Lead" value={detail.lead?.fullName || "Untitled Lead"} note={detail.lead?.email || "No email recorded"} />
                    <DetailCard label="Client" value={detail.client?.clientName || "Client not yet created"} note={detail.client?.clientEmail || "Will be created on acceptance"} />
                    <DetailCard label="Agreement" value={detail.agreement?.status || "Not created"} note={detail.agreement?.agreementUrl || "Agreement will be generated after acceptance"} />
                    <DetailCard label="Payment" value={detail.payment?.status || "Not created"} note={detail.payment?.invoiceStatus || "Payment workflow starts after agreement"} />
                  </div>

                  <div className="rounded-2xl border border-[rgba(217,74,22,.16)] bg-white p-4">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#D94A16]">Line Items</p>
                    <div className="mt-4 grid gap-3">
                      {detail.proposal.lineItems.length ? (
                        detail.proposal.lineItems.map((item) => (
                          <div key={item.id} className="rounded-xl border border-[rgba(217,74,22,.12)] bg-[#fffdf8] p-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-black uppercase tracking-[0.12em] text-[#D94A16]">{item.lineType}</p>
                                <h3 className="mt-1 font-display text-2xl font-bold text-[#071A2F]">{item.name}</h3>
                                <p className="mt-2 text-sm font-semibold leading-6 text-[#5c5246]">{item.description || "No description provided."}</p>
                              </div>
                              <span className="rounded-full border border-[rgba(217,74,22,.18)] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#071A2F]">
                                {item.quantity} × {formatMoney(item.unitCents)}
                              </span>
                            </div>
                            <p className="mt-3 text-sm font-bold text-[#071A2F]">Amount: {formatMoney(item.amountCents)}</p>
                          </div>
                        ))
                      ) : (
                        <EmptyState title="No line items recorded" description="Proposal line items will appear here once they are saved with the proposal record." />
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[rgba(217,74,22,.16)] bg-white p-4">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#D94A16]">Timeline Events</p>
                    <div className="mt-4 grid gap-3">
                      {detail.timeline.length ? (
                        detail.timeline.map((event) => (
                          <div key={event.id} className="rounded-xl border border-[rgba(217,74,22,.12)] bg-[#fffdf8] p-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-black uppercase tracking-[0.12em] text-[#D94A16]">{event.activityType}</p>
                                <p className="mt-1 text-sm font-semibold text-[#5c5246]">{event.notes || "Timeline event recorded."}</p>
                              </div>
                              <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#D94A16]">{formatDisplayDate(event.createdAt)}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <EmptyState title="Timeline events appear here" description="Acceptance, agreement, and conversion events will populate this panel as the proposal moves forward." />
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[rgba(34,224,208,.18)] bg-[rgba(7,26,47,.04)] p-4">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#D94A16]">Proposal to Client Conversion</p>
                    <p className="mt-2 text-sm font-semibold leading-7 text-[#5c5246]">
                      When the proposal is accepted, Truevance automatically creates or links a client record, preserves the proposal
                      history, logs a conversion event, and prepares the handoff to Project Center.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {detail.client?.id ? (
                        <Link to={`/admin/forms-client/${detail.client.id}`} className="admin-primary-button focus-ring">
                          Open Client Record
                        </Link>
                      ) : (
                        <span className="inline-flex min-h-11 items-center rounded-full border border-[rgba(34,224,208,.24)] px-4 text-sm font-bold text-[#071A2F]">
                          Client record will appear after acceptance
                        </span>
                      )}
                      <Link to="/admin/truevance-project-center" className="admin-secondary-button focus-ring">
                        Open Project Center
                      </Link>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[rgba(217,74,22,.16)] bg-[#fffdf8] p-4">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#D94A16]">Action Notes</p>
                    <textarea
                      rows={4}
                      value={actionNotes}
                      onChange={(event) => setActionNotes(event.target.value)}
                      className="admin-input mt-3"
                      placeholder="Optional note for sent, revision requested, accepted, or declined actions..."
                    />
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button type="button" disabled={saving} onClick={() => void updateProposal("sent")} className="admin-primary-button focus-ring">
                        <Send className="size-4" aria-hidden="true" />
                        Mark Sent
                      </button>
                      <button type="button" disabled={saving} onClick={() => void updateProposal("revision_requested")} className="admin-secondary-button focus-ring">
                        <RefreshCw className="size-4" aria-hidden="true" />
                        Request Revision
                      </button>
                      <button type="button" disabled={saving} onClick={() => void updateProposal("accepted")} className="admin-secondary-button focus-ring">
                        <CheckCircle2 className="size-4" aria-hidden="true" />
                        Accept & Convert
                      </button>
                      <button type="button" disabled={saving} onClick={() => void updateProposal("declined")} className="admin-secondary-button focus-ring">
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              ) : detailLoading ? (
                <EmptyState title="Loading selected proposal" description="The selected proposal record is being loaded from D1." />
              ) : (
                <EmptyState
                  title="Select a proposal record"
                  description="Choose a record on the left to review its version history, conversion state, and client handoff."
                />
              )}
            </article>
          </section>

          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <WorkflowCard title="Proposal Draft" description="Create a draft proposal for an existing lead." />
            <WorkflowCard title="Proposal Approval" description="Mark the proposal ready to send after internal review." />
            <WorkflowCard title="Client Conversion" description="Accepted proposals automatically create or link client records." />
            <WorkflowCard title="Project Handoff" description="Accepted proposals prepare the handoff into Project Center." />
          </section>

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => navigate("/admin/truevance-business-center")}
              className="focus-ring inline-flex min-h-12 items-center justify-center rounded-lg bg-[#FF6A2B] px-5 text-sm font-extrabold text-[#050505] transition hover:bg-[#FF6A2B]"
            >
              Back to Truevance Business Center
            </button>
            <Link
              to="/admin/truevance-business-center"
              className="focus-ring inline-flex min-h-12 items-center justify-center rounded-lg border border-[rgba(34,224,208,.32)] px-5 text-sm font-extrabold text-[#22E0D0] transition hover:bg-white/10"
            >
              Open Business Center Home
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function SectionHeading({ icon: Icon, title, subtitle, dark = false }: { icon: typeof Sparkles; title: string; subtitle: string; dark?: boolean }) {
  return (
    <div className="border-b border-[rgba(217,74,22,.14)] pb-4">
      <p className={`flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] ${dark ? "text-[#22E0D0]" : "text-[#D94A16]"}`}>
        <Icon className={`size-4 ${dark ? "text-[#17D4C4]" : "text-[#D94A16]"}`} aria-hidden="true" />
        {TRUEVANCE_BRAND_NAME}
      </p>
      <h2 className={`mt-1 font-display text-3xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</h2>
      <p className={`mt-3 max-w-4xl text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]/76" : "text-[#5c5246]"}`}>{subtitle}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-2 text-sm font-bold text-[#071A2F]">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{label}</span>
      {children}
    </label>
  );
}

function NumberInput({
  value,
  onChange,
  prefix,
}: {
  value: string;
  onChange: (value: string) => void;
  prefix?: string;
}) {
  return (
    <div className="flex items-center rounded-2xl border border-[rgba(217,74,22,.16)] bg-white px-4 transition focus-within:border-[#FF6A2B]">
      {prefix ? <span className="pr-2 text-sm font-black text-[#D94A16]">{prefix}</span> : null}
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full bg-transparent py-3 text-[#071A2F] outline-none placeholder:text-[#5c5246]/45"
      />
    </div>
  );
}

function MetricCard({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string | number;
  note: string;
  tone: "light" | "dark";
}) {
  return (
    <div className={`rounded-2xl border p-4 ${tone === "dark" ? "truevance-business-card--dark" : "truevance-business-card--light"} truevance-business-card`}>
      <p className={`text-xs font-black uppercase tracking-[0.14em] ${tone === "dark" ? "text-[#22E0D0]" : "text-[#D94A16]"}`}>{label}</p>
      <p className={`mt-2 font-display text-3xl font-bold ${tone === "dark" ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{value}</p>
      <p className={`mt-2 text-sm font-semibold leading-6 ${tone === "dark" ? "text-[#E8EEF4]/74" : "text-[#5c5246]"}`}>{note}</p>
    </div>
  );
}

function DetailCard({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(217,74,22,.16)] bg-[#fffdf8] p-4">
      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{label}</p>
      <p className="mt-2 font-display text-2xl font-bold text-[#071A2F]">{value}</p>
      <p className="mt-2 text-sm font-semibold leading-6 text-[#5c5246]">{note}</p>
    </div>
  );
}

function EmptyState({ title, description, dark = false }: { title: string; description: string; dark?: boolean }) {
  return (
    <div
      className={`rounded-2xl border border-dashed p-4 ${
        dark ? "border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)]" : "border-[rgba(217,74,22,.2)] bg-[#fffdf8]"
      }`}
    >
      <p className={`font-display text-2xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</p>
      <p className={`mt-2 text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]/74" : "text-[#5c5246]"}`}>{description}</p>
    </div>
  );
}

function WorkflowCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="truevance-business-card truevance-business-card--dark rounded-2xl p-4">
      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{title}</p>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{description}</p>
    </div>
  );
}
